import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Calendar } from '../components/Icons.jsx';

const topics = [
  {
    week: 'This week',
    title: 'AI-assisted triage in NHS primary care',
    summary: 'NHS England has rolled out an AI symptom-checker integration in 30+ GP practices, reducing average triage time but raising debate on accountability when AI mis-triage occurs.',
    why: 'Touches on autonomy, clinical judgement, and the GMC\'s evolving guidance on AI use. Likely to appear in MMI ethical stations and panel questions on "future of medicine".',
    angle: 'Don\'t say "AI will replace doctors" — interviewers expect nuance. Frame it as augmentation, with accountability still resting on the clinician.',
    tag: 'AI · Ethics'
  },
  {
    week: 'This week',
    title: 'Junior doctor pay & retention',
    summary: 'BMA-government negotiations continue after the 2025 strikes. Retention rates among Foundation doctors have declined 14% year-on-year, with many citing burnout and pay erosion as primary reasons.',
    why: 'Pay-and-retention is the #1 NHS interview hot topic. Tests whether you understand both the doctor\'s perspective and the public-purse argument.',
    angle: 'Don\'t pick a side aggressively. Show you can hold both: doctors deserve fair pay; the NHS budget is finite. The intellectual honesty wins the answer.',
    tag: 'Workforce · NHS'
  },
  {
    week: 'Recent',
    title: 'Wegovy & weight-loss drugs on the NHS',
    summary: 'NICE expanded NHS prescribing criteria for semaglutide-based weight-loss medications in late 2025, while warning about supply shortages and the rise of unregulated online sellers.',
    why: 'Cuts across NHS resource allocation, patient autonomy, and the ethics of treating "lifestyle" conditions. Classic ethical-dilemma fodder.',
    angle: 'Frame as a population-health calculation. Mention obesity\'s long-term cost to the NHS, the equity question (who gets access), and the danger of demand outpacing supply.',
    tag: 'NHS · Public health'
  },
  {
    week: 'Recent',
    title: 'Mental health waiting times',
    summary: 'CAMHS (Child &amp; Adolescent Mental Health Services) wait times in some regions now exceed 18 months. Adult IAPT performance has improved but rural access remains poor.',
    why: 'Mental health is often raised in personal-statement reflections and interview discussions on NHS inequality.',
    angle: 'Have one or two structural causes ready (workforce shortages, demand surge post-pandemic) and one practical example of where access is improving.',
    tag: 'Mental health'
  },
  {
    week: 'Recent',
    title: 'Antimicrobial resistance & stewardship',
    summary: 'New NICE guidance restricts broad-spectrum antibiotic use in primary care after WHO classified AMR as a "top-tier" global health threat.',
    why: 'Sits at the intersection of microbiology (a topic schools assume you find interesting), public health, and individual prescribing.',
    angle: 'Mention the One Health framing: antibiotic use in animal agriculture is part of the picture. Earns nuance points.',
    tag: 'Public health'
  },
  {
    week: 'Background',
    title: 'Assisted dying legislation',
    summary: 'The UK Parliament is reviewing the Terminally Ill Adults (End of Life) Bill, with safeguards modelled partly on Oregon\'s framework. The BMA shifted from "opposed" to "neutral" in 2024.',
    why: 'A reliable ethics-station topic. Tests if you can articulate principlism: autonomy, beneficence, non-maleficence, justice — without picking a personal side.',
    angle: 'Don\'t share your personal view unless explicitly asked. Show you can articulate both sides via the four principles. Acknowledge the protected-population concern.',
    tag: 'Ethics'
  }
];

export default function HotTopics() {
  const [email, setEmail] = useState('');
  const [signedUp, setSignedUp] = useState(false);

  return (
    <>
      <div className="page-head">
        <div className="kicker" style={{ color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, fontSize: 12 }}>NHS Hot Topics · Updated weekly</div>
        <h1>What to talk about in your medical interview</h1>
        <p className="subtitle">Interview-ready NHS hot topics. Brief, why it matters, the angle that wins the room.</p>
      </div>

      <div className="card" style={{ marginBottom: 24, background: 'linear-gradient(135deg, var(--accent-soft), #fff)' }}>
        {signedUp ? (
          <div style={{ textAlign: 'center', padding: 12 }}>
            <strong style={{ fontSize: 16 }}>You're in.</strong>
            <p style={{ margin: '4px 0 0', color: 'var(--text-muted)' }}>We'll email the new batch every Sunday morning.</p>
          </div>
        ) : (
          <>
            <h3 style={{ margin: '0 0 6px', fontSize: 18 }}>Get the weekly digest</h3>
            <p style={{ margin: '0 0 12px', color: 'var(--text-2)', fontSize: 14 }}>Every Sunday: 5–6 new topics, briefs, interview angles. Free, no spam, unsubscribe anytime.</p>
            <form
              onSubmit={(e) => { e.preventDefault(); if (email.includes('@')) setSignedUp(true); }}
              style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}
            >
              <input
                type="email" placeholder="your.email@example.com"
                value={email} onChange={(e) => setEmail(e.target.value)}
                style={{ flex: 1, minWidth: 220, padding: '12px 14px', border: '1px solid var(--border-strong)', borderRadius: 10, fontSize: 14, background: '#fff' }}
              />
              <button type="submit" className="btn btn-primary">Subscribe <ArrowRight width="16" height="16" /></button>
            </form>
          </>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {topics.map((t) => (
          <article key={t.title} className="card hoverable" style={{ padding: 0 }}>
            <div style={{ padding: '22px 24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
                <span style={{ fontSize: 12, color: 'var(--accent)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{t.week} · {t.tag}</span>
              </div>
              <h2 style={{ margin: '6px 0 12px', fontSize: 22, fontWeight: 700, letterSpacing: '-0.01em' }}>{t.title}</h2>
              <p style={{ color: 'var(--text-2)', margin: '0 0 14px' }}>{t.summary}</p>
              <div className="r-grid-2">
                <div style={{ padding: 14, background: 'var(--card-soft)', borderRadius: 10 }}>
                  <strong style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Why it matters</strong>
                  <p style={{ margin: '4px 0 0', color: 'var(--text-2)', fontSize: 14 }}>{t.why}</p>
                </div>
                <div style={{ padding: 14, background: 'var(--accent-soft)', borderRadius: 10 }}>
                  <strong style={{ fontSize: 12, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>The winning angle</strong>
                  <p style={{ margin: '4px 0 0', color: 'var(--text-2)', fontSize: 14 }}>{t.angle}</p>
                </div>
              </div>
            </div>
          </article>
        ))}
      </div>

      <section className="parents-cta" style={{ marginTop: 32 }}>
        <div>
          <h2>Run mock interviews with a Medic Mind tutor</h2>
          <p>Reading topics is one thing — defending a position under cross-examination is another. Our interview coaches put you through real MMI conditions, including ethics stations on these exact topics.</p>
        </div>
        <a className="btn">See interview coaching <ArrowRight width="16" height="16" /></a>
      </section>
    </>
  );
}
